import { useState, useEffect, useCallback } from 'react';
import { cacheService } from '../services/cacheService';
import axiosInstance from '../services/axiosInstance';
import type { CachedPatient, CachedDoctor, CachedMedicalRecord } from '../services/cacheService';
import { useCache } from '../hooks/useCache';
import type { UseCachedApiOptions } from '../hooks/useCachedApi';

// Hook pour les patients
export function useCachedPatient(id: number | null) {
  const [patient, setPatient] = useState<CachedPatient | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPatient = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await cacheService.getPatient(id || undefined);
      setPatient(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement du patient');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPatient();
  }, [fetchPatient]);

  const refetch = useCallback(() => {
    cacheService.invalidatePatientCache(id || undefined);
    fetchPatient();
  }, [id, fetchPatient]);

  return { patient, loading, error, refetch };
}

// Hook pour la recherche de patients
export function useCachedPatientSearch() {
  const [results, setResults] = useState<{ content: CachedPatient[]; total: number }>({ content: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchPatients = useCallback(async (query: string, page = 0, size = 10) => {
    setLoading(true);
    setError(null);

    try {
      const data = await cacheService.searchPatients(query, page, size);
      setResults(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la recherche');
    } finally {
      setLoading(false);
    }
  }, []);

  return { results, loading, error, searchPatients };
}

// Hook pour les médecins
export function useCachedDoctor(id: number | null) {
  const [doctor, setDoctor] = useState<CachedDoctor | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDoctor = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await cacheService.getDoctor(id);
      setDoctor(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement du médecin');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDoctor();
  }, [fetchDoctor]);

  const refetch = useCallback(() => {
    cacheService.invalidateDoctorCache(id || undefined);
    fetchDoctor();
  }, [id, fetchDoctor]);

  return { doctor, loading, error, refetch };
}

// Hook pour la liste des médecins
export function useCachedDoctors() {
  const [doctors, setDoctors] = useState<CachedDoctor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await cacheService.getDoctors();
      setDoctors(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des médecins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  const refetch = useCallback(() => {
    cacheService.invalidateDoctorCache();
    fetchDoctors();
  }, [fetchDoctors]);

  return { doctors, loading, error, refetch };
}

// Hook pour les dossiers médicaux
export function useCachedMedicalRecord(id: number | null) {
  const [record, setRecord] = useState<CachedMedicalRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecord = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const data = await cacheService.getMedicalRecord(id);
      setRecord(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement du dossier médical');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRecord();
  }, [fetchRecord]);

  const refetch = useCallback(() => {
    cacheService.invalidateMedicalRecordCache(id || undefined);
    fetchRecord();
  }, [id, fetchRecord]);

  return { record, loading, error, refetch };
}

// Hook pour les dossiers médicaux d'un patient
export function useCachedPatientMedicalRecords(patientId: number | null) {
  const [records, setRecords] = useState<{ content: CachedMedicalRecord[]; total: number }>({ content: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRecords = useCallback(async (page = 0, size = 10) => {
    if (!patientId) return;

    setLoading(true);
    setError(null);

    try {
      const data = await cacheService.getMedicalRecordsByPatient(patientId, page, size);
      setRecords(data);
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des dossiers médicaux');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      fetchRecords();
    }
  }, [fetchRecords, patientId]);

  const refetch = useCallback(() => {
    cacheService.invalidateMedicalRecordCache();
    fetchRecords();
  }, [fetchRecords]);

  return { records, loading, error, refetch, fetchPage: fetchRecords };
}

// Hook pour les données paginées avec cache
export function useCachedPaginatedApi<T = any>(
  baseUrl: string,
  options: UseCachedApiOptions & { pageSize?: number } = {}
) {
  const { pageSize = 10, ...apiOptions } = options;
  const cache = useCache<{ data: T[]; total: number; page: number }>({ ttl: apiOptions.ttl });

  const fetchPage = useCallback(async (page: number, params?: any) => {
    const url = `${baseUrl}?page=${page - 1}&size=${pageSize}${params ? `&${new URLSearchParams(params).toString()}` : ''}`;
    const cacheKey = `${baseUrl}_page_${page}_${JSON.stringify(params || {})}`;

    // Try cache first
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    try {
      const response = await axiosInstance.get(url);
      const data = {
        data: response.data.content || response.data,
        total: response.data.totalElements || response.data.length,
        page,
      };

      cache.set(cacheKey, data);
      return data;
    } catch (error) {
      console.error(`Paginated API Error for ${url}:`, error);
      throw error;
    }
  }, [baseUrl, pageSize, cache]);

  const invalidateCache = useCallback(() => {
    // Clear all paginated cache for this base URL
    const stats = cache.getCacheStats();
    console.log('Invalidating cache for:', baseUrl, stats);
    cache.clear();
  }, [baseUrl, cache]);

  return {
    fetchPage,
    invalidateCache,
    getCacheStats: cache.getCacheStats,
  };
}

// Hook pour la gestion du cache
export function useCacheManager() {
  const clearAllCaches = useCallback(() => {
    cacheService.clearAllCaches();
  }, []);

  const getCacheStats = useCallback(() => {
    return cacheService.getCacheStats();
  }, []);

  return {
    clearAllCaches,
    getCacheStats,
    invalidatePatientCache: cacheService.invalidatePatientCache.bind(cacheService),
    invalidateDoctorCache: cacheService.invalidateDoctorCache.bind(cacheService),
    invalidateMedicalRecordCache: cacheService.invalidateMedicalRecordCache.bind(cacheService),
  };
}
